import { Flex } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { v4 as uuid } from 'uuid';
import { SidebarContent } from 'src/UI/app-container/SidebarContent';
import { FlowHeader } from 'src/UI/generate-flow/FlowHeader';
import { PillButton } from 'src/UI/generate-flow/PillButton';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { SearchPill } from 'src/UI/generate-flow/SearchPill';
import { SiestaBarLayout } from 'src/UI/app-container/sidebar/SiestaBarLayout';
import { useContentMeta } from 'src/UI/app-container';
import { ChatMode } from 'src/UI/app-container/chat/types';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { DataExportClientStep } from './steps/DataExportClientStep';
import { DataExportGenerateStep } from './steps/DataExportGenerateStep';
import { DataExportProcessingStep } from './steps/DataExportProcessingStep';
import { SelectConfigStep } from './steps/SelectConfigStep';
import { DataExportSidebar } from './components/DataExportSidebar';
import { DataExportFlowData, DataExportStepId, DataExportStepProps } from './types';

const initialData: DataExportFlowData = {
  client: undefined,
  family: undefined,
  isFamily: false,
  includeSensitiveFields: false,
  exportFormat: 'DOCX',
};

type StepDef = {
  id: DataExportStepId;
  title: string;
  disabledButtonlabel: string;
  render: (props: DataExportStepProps) => React.ReactNode;
  canContinue?: (data: DataExportFlowData) => boolean;
  canFilter?: boolean;
  continueLabel?: string;
};

export default function DataExportFlowPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const backHref = location.state?.fromPath ?? `/clients/all`;

  const { client: currentClient, clientId } = useCurrentClient();
  useContentMeta({ pageType: 'client', entityId: clientId, chatMode: ChatMode.CLIENT });

  const flowId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('flowId');
    if (fromUrl) return fromUrl;
    const fromState = location.state?.flowId;
    if (fromState) return fromState;
    const id = uuid();
    const url = new URL(window.location.href);
    url.searchParams.set('flowId', id);
    window.history.replaceState(null, '', url);
    return id;
  }, [location.state?.flowId]);

  const storageKey = `data-export-flow-${flowId}`;

  const [data, setData] = useState<DataExportFlowData>(() => {
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return initialData;
      }
    }
    return initialData;
  });

  const [stepIndex, setStepIndex] = useState(() => {
    const saved = sessionStorage.getItem(`${storageKey}-step`);
    return saved ? parseInt(saved, 10) : 0;
  });

  const [filter, setFilter] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(data));
  }, [data, storageKey]);

  useEffect(() => {
    sessionStorage.setItem(`${storageKey}-step`, String(stepIndex));
  }, [stepIndex, storageKey]);

  useEffect(() => {
    setFilter('');
  }, [stepIndex]);

  const steps: StepDef[] = useMemo(
    () => [
      {
        id: 'client' as const,
        title: data.isFamily ? 'Select family' : 'Select client',
        disabledButtonlabel: data.isFamily ? 'Select the family' : 'Select the client',
        canContinue: d => (d.isFamily ? !!d.family?.id : !!d.client?.id),
        canFilter: true,
        render: props => <DataExportClientStep {...props} preselectClientId={currentClient?.id} />,
      },
      {
        id: 'config' as const,
        title: 'Select configuration',
        disabledButtonlabel: 'Select a configuration',
        canContinue: (d: DataExportFlowData) => !!d.configId,
        render: (props: DataExportStepProps) => <SelectConfigStep {...props} />,
      },
      {
        id: 'generate' as const,
        title: 'Configure options',
        disabledButtonlabel: 'Configure options',
        canContinue: () => true,
        continueLabel: 'Generate data export',
        render: props => <DataExportGenerateStep {...props} />,
      },
      {
        id: 'processing' as const,
        title: 'Processing',
        disabledButtonlabel: 'Processing...',
        canContinue: () => true,
        canFilter: false,
        render: props => (
          <DataExportProcessingStep {...props} onSubmittingChange={setIsSubmitting} />
        ),
      },
    ],
    [currentClient?.id, data.isFamily],
  );

  const current = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const canContinue = (current.canContinue ? current.canContinue(data) : true) && !data.isLoading;
  const canFilter = current.canFilter ?? false;

  const resetCurrentStepSelection = () => {
    setData(prev => {
      switch (current.id) {
        case 'config':
          return { ...prev, configId: undefined, configDisplayName: undefined };
        case 'generate':
          return { ...prev, includeSensitiveFields: false, exportFormat: 'DOCX' };
        default:
          return prev;
      }
    });
  };

  const goBack = () => {
    if (!isFirst) setStepIndex(i => i - 1);
    resetCurrentStepSelection();
  };

  const goNext = () => {
    if (!isLast) setStepIndex(i => i + 1);
  };

  const clearFlowStorage = () => {
    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem(`${storageKey}-step`);
  };

  const onClose = () => {
    clearFlowStorage();
    navigate(backHref);
  };

  const continueLabel = !canContinue
    ? current.disabledButtonlabel
    : (current.continueLabel ?? 'Continue');

  const searchPlaceholder = data.isFamily ? 'Search family by name' : 'Search client by name/email';

  return (
    <>
      <Flex direction="column" h="100%" pos="relative">
        <FlowLayout
          header={
            !isLast ? (
              <FlowHeader onClose={onClose} onBack={stepIndex > 0 ? goBack : undefined} />
            ) : undefined
          }
          body={
            <FlowBody
              footerAlign="center"
              footer={
                <Flex direction="column" w={360}>
                  {current.id !== 'processing' && (
                    <PillButton
                      fullWidth
                      disabled={!canContinue}
                      label={continueLabel}
                      onClick={goNext}
                    />
                  )}
                </Flex>
              }
            >
              {current.render({
                data,
                setData,
                onNext: goNext,
                filter,
                searchBar: canFilter ? (
                  <SearchPill value={filter} onChange={setFilter} placeholder={searchPlaceholder} />
                ) : undefined,
              })}
            </FlowBody>
          }
        />
      </Flex>
      <SidebarContent>
        <SiestaBarLayout>
          <DataExportSidebar
            stepIndex={stepIndex}
            steps={steps}
            data={data}
            currentStepId={current.id}
            isSubmitting={isSubmitting}
          />
        </SiestaBarLayout>
      </SidebarContent>
    </>
  );
}
